import {
  getSecurityOverview,
  getTrafficReport,
  listAdminAlerts,
  listAuthEvents,
  listBlockedIps,
  listSecurityEvents,
} from './adminApi';
import { getApiErrorMessage } from './apiError';
import type {
  AdminAlert,
  AuthEvent,
  AuthEventsFilters,
  Paginated,
  SecurityEvent,
  SecurityEventsFilters,
  SecurityOverview,
  TrafficReport,
} from '../types/admin';

/** Admin list endpoints cap `limit` at 100. */
export const SECURITY_EXPORT_PAGE_SIZE = 100;
/** Hard stop so a huge table cannot hang the tab. 20 × 100 = 2 000 rows per list. */
export const SECURITY_EXPORT_MAX_PAGES = 20;

export const SECURITY_HOURS_VALUES = new Set(['1', '6', '24', '168']);

export function parseSecurityHours(raw: string | null | undefined): number {
  if (raw && SECURITY_HOURS_VALUES.has(raw)) return Number(raw);
  return 24;
}

export function windowRange(hours: number, now = new Date()): { from: string; to: string } {
  const safeHours = Number.isFinite(hours) && hours > 0 ? hours : 24;
  return {
    from: new Date(now.getTime() - safeHours * 60 * 60 * 1000).toISOString(),
    to: now.toISOString(),
  };
}

export function securityExportFilename(kind: string, hours?: number, now = new Date()): string {
  const stamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const window = hours != null ? `-${hours}h` : '';
  return `kol-security-${kind}${window}-${stamp}Z.json`;
}

export async function fetchAllPages<T>(
  load: (page: number) => Promise<Paginated<T>>,
  opts?: { maxPages?: number }
): Promise<{ items: T[]; total: number; truncated: boolean }> {
  const maxPages = opts?.maxPages ?? SECURITY_EXPORT_MAX_PAGES;
  const first = await load(1);
  const items = [...first.items];
  const total = first.pagination?.total ?? items.length;
  const reportedPages = first.pagination?.totalPages ?? 1;
  const pagesToFetch = Math.min(Math.max(1, reportedPages), maxPages);
  for (let page = 2; page <= pagesToFetch; page++) {
    const next = await load(page);
    items.push(...next.items);
  }
  return { items, total, truncated: reportedPages > maxPages };
}

export interface SecuritySnapshot {
  exportedAt: string;
  hours: number;
  from: string;
  to: string;
  truncated: string[];
  overview: SecurityOverview;
  events: SecurityEvent[];
  authEvents: AuthEvent[];
  blockedIps: Awaited<ReturnType<typeof listBlockedIps>>['items'];
  alerts: { unacknowledged: AdminAlert[]; acknowledged: AdminAlert[] };
  trafficByIp: Record<string, TrafficReport | { error: string }>;
}

async function loadEvents(filters: SecurityEventsFilters) {
  return fetchAllPages((page) =>
    listSecurityEvents({ ...filters, page, limit: SECURITY_EXPORT_PAGE_SIZE })
  );
}

async function loadAuth(filters: AuthEventsFilters) {
  return fetchAllPages((page) =>
    listAuthEvents({ ...filters, page, limit: SECURITY_EXPORT_PAGE_SIZE })
  );
}

async function loadAlerts(acknowledged: '0' | '1') {
  return fetchAllPages((page) =>
    listAdminAlerts({ acknowledged, page, limit: SECURITY_EXPORT_PAGE_SIZE })
  );
}

async function loadTrafficByIp(ips: string[], hours: number): Promise<SecuritySnapshot['trafficByIp']> {
  const unique = [...new Set(ips.filter(Boolean))];
  const trafficByIp: SecuritySnapshot['trafficByIp'] = {};
  await Promise.all(
    unique.map(async (ip) => {
      try {
        trafficByIp[ip] = await getTrafficReport({ ip }, hours);
      } catch (err) {
        trafficByIp[ip] = { error: getApiErrorMessage(err, 'Failed to load traffic') };
      }
    })
  );
  return trafficByIp;
}

/** Overview tab payload: totals, top IPs, and a route breakdown for each listed IP. */
export async function collectOverviewExport(hours: number, now = new Date()) {
  const { from, to } = windowRange(hours, now);
  const overview = await getSecurityOverview(hours);
  const trafficByIp = await loadTrafficByIp(
    overview.topIps.map((row) => row.ip),
    hours
  );
  return {
    exportedAt: now.toISOString(),
    hours,
    from,
    to,
    overview,
    trafficByIp,
  };
}

/**
 * Everything on /admin/security for the overview window: totals, events, sign-ins,
 * blocked IPs, alerts, and per-IP route breakdowns for the top IPs currently listed.
 */
export async function collectSecuritySnapshot(hours: number, now = new Date()): Promise<SecuritySnapshot> {
  const { from, to } = windowRange(hours, now);
  const truncated: string[] = [];

  const overview = await getSecurityOverview(hours);

  const [events, authEvents, blocked, unacked, acked, trafficByIp] = await Promise.all([
    loadEvents({ from, to }),
    loadAuth({ from, to }),
    listBlockedIps(),
    loadAlerts('0'),
    loadAlerts('1'),
    loadTrafficByIp(
      overview.topIps.map((row) => row.ip),
      hours
    ),
  ]);

  if (events.truncated) truncated.push(`events (kept ${events.items.length} of ${events.total})`);
  if (authEvents.truncated) truncated.push(`authEvents (kept ${authEvents.items.length} of ${authEvents.total})`);
  if (unacked.truncated) truncated.push(`alerts.unacknowledged (kept ${unacked.items.length} of ${unacked.total})`);
  if (acked.truncated) truncated.push(`alerts.acknowledged (kept ${acked.items.length} of ${acked.total})`);

  return {
    exportedAt: now.toISOString(),
    hours,
    from,
    to,
    truncated,
    overview,
    events: events.items,
    authEvents: authEvents.items,
    blockedIps: blocked.items,
    alerts: { unacknowledged: unacked.items, acknowledged: acked.items },
    trafficByIp,
  };
}

export async function collectEventsExport(filters: SecurityEventsFilters) {
  return loadEvents(filters);
}

export async function collectAuthEventsExport(filters: AuthEventsFilters) {
  return loadAuth(filters);
}

export async function collectAlertsExport(acknowledged?: '0' | '1') {
  if (acknowledged === '0' || acknowledged === '1') return loadAlerts(acknowledged);
  const [open, done] = await Promise.all([loadAlerts('0'), loadAlerts('1')]);
  return {
    items: [...open.items, ...done.items],
    total: open.total + done.total,
    truncated: open.truncated || done.truncated,
  };
}
